import { Store } from "redux";
import { endLoadingState, loadingTemplate, startLoadingState } from "../../components/ticketing/basket-v2/basketLoading";
import { logError, reportError } from "../../components/ticketing/basket-v2/errorReporting";
import { constants } from "../../components/ticketing/common";
import { deleteJsonWithResponse, EventEmitter, postJsonWithResponse, trackEvent } from "../../utils";
import { classes, events } from "../constants";
import { ADD_POINTS, REMOVE_POINTS } from "./store";
import { observeStore } from "./utils";

export class Reward4Racing {
  rewards: HTMLElement;
  rewardsSection: HTMLElement;
  rewardsEmailCta: HTMLElement;
  rewardsSignupCta: HTMLElement;
  rewardsSelector: string;
  store: Store;
  emailInput: HTMLInputElement;
  applyButton: HTMLButtonElement;
  pointsSelect: HTMLSelectElement;
  remainingPoints: HTMLSpanElement;
  pointsAvailable: HTMLElement;
  totalPoints: number;
  defaultError = { code: 500, message: "Error applying/removing reward points" };
  loadingPointsMessage: string;
  addingPointsMessage: string;

  constructor(selector: string, store: Store) {
    this.store = store;
    this.rewardsSelector = selector;
    this.loadingPointsMessage = loadingTemplate("Please wait");
    this.addingPointsMessage = loadingTemplate("Adding reward points");

    this.emailInput = document.querySelector<HTMLInputElement>("#email");
    if (this.emailInput != null) {
      this.emailInput.addEventListener("change", async () => {

        if (this.rewardsSection == null ||
          this.rewards == null ||
          this.rewardsEmailCta == null ||
          this.rewardsSignupCta == null)
        {
          return;
        }
        await this.removeAppliedRewards();
        if (this.emailInput.value) {
          const isRegistered = await this.rewardsRegistered(this.emailInput.value);
          this.rewardsEmailCta.classList.add(classes.HIDDEN);
          if (isRegistered) {
            await this.refreshRewards();
            this.rewards.classList.remove(classes.HIDDEN);
            this.rewardsSignupCta.classList.add(classes.HIDDEN);
          } else {
            this.rewards.classList.add(classes.HIDDEN);
            this.rewardsSignupCta.classList.remove(classes.HIDDEN);
          }
        } else {
          this.rewards.classList.add(classes.HIDDEN);
          this.rewardsSignupCta.classList.add(classes.HIDDEN);
          this.rewardsEmailCta.classList.remove(classes.HIDDEN);
        }
      });
    }

    this.parseElements();
    this.bindEvents();

    observeStore(this.store, (state) => (state as any).delivery, this.handleDeliveryChange);
    observeStore(this.store, (state) => (state as any).promo, this.handleDiscountChange);
    observeStore(this.store, (state) => (state as any).vouchers, this.handleDiscountChange);
  }

  parseElements() {
    this.rewards = document.querySelector(this.rewardsSelector);

    if (!this.rewards) {
      return;
    }
    this.rewardsSection = document.querySelector("[data-rewards-section]");
    this.applyButton = this.rewards.querySelector<HTMLButtonElement>("[data-rewards-racing-apply]");
    this.pointsSelect = this.rewards.querySelector<HTMLSelectElement>("[data-rewards-racing-select]");
    this.remainingPoints = this.rewards.querySelector<HTMLSpanElement>("[data-rewards-racing-remaining]");
    this.pointsAvailable = this.rewards.querySelector("[data-rewards-racing-points-available]");
    this.rewardsEmailCta = document.querySelector("[data-rewards-email-cta]");
    this.rewardsSignupCta = document.querySelector("[data-rewards-signup-cta]");
    this.totalPoints = parseInt(this.rewards.dataset.rewardsRacingTotalPoints, 10);
  }

  bindEvents() {
    if (!this.rewards) return;

    this.applyButton.addEventListener("click", this.handleApplyPoints);
    this.rewards.addEventListener("click", this.handleRemovePoints, true);
    this.pointsSelect.addEventListener("change", this.handlePointsChange);
  }

  getSelectedPoints() {
    const selectedPoints = this.pointsSelect.options[this.pointsSelect.selectedIndex];
    const numberOfPoints = parseInt(selectedPoints.value, 10);
    const amount = parseFloat(selectedPoints.dataset.pointsCurrency);

    return { numberOfPoints, amount };
  }

  getAppliedPoints() {
    if (!this.rewards) return 0;

    const pointsAppliedEl = this.rewards.querySelector<HTMLElement>("[data-points-added]");
    if (!pointsAppliedEl) return 0;

    return parseInt(pointsAppliedEl.dataset.pointsAdded);
  }

  rewardsRegistered = async (email) => {
    try {
      const url = `${constants.urls.payment.rewards_registered}?email=${email}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        logError(500, "Error checking if email is registered for rewards");
        return false;
      }

      const registeredResult: any = await response.json();

      EventEmitter.emit(events.R4R_REGISTERED_CHECK, registeredResult.registered);

      return registeredResult.registered;
    } catch (err) {
      logError(500, err, "Error checking if email is registered for rewards");
      return false;
    }
  };

  refreshRewards = async () => {
    try {
      this.rewards = document.querySelector(this.rewardsSelector);
      if (this.rewards) {
        startLoadingState(this.loadingPointsMessage, ".basket-overlay");

        let url = constants.urls.payment.reward_points;
        if (this.emailInput != null) {
          url += `?email=${this.emailInput.value}`;
        }

        const response = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          reportError(400, "There was an error refreshing reward points");
          return;
        }

        const rewardsContent = await response.text();
        this.rewards.outerHTML = rewardsContent;

        EventEmitter.emit(events.R4R_REFRESH);

        this.parseElements();
        this.bindEvents();
      }
    } catch (e) {
      reportError(this.defaultError.code, e.message ?? this.defaultError.message);
    }

    endLoadingState(".basket-overlay");
  };

  removeAppliedRewards = async () => {
    const numberOfPoints = this.getAppliedPoints();
    if (numberOfPoints > 0) {
      const pointsRequest = { points: numberOfPoints, email: this.emailInput.value };
      const response = await deleteJsonWithResponse(constants.urls.payment.reward_points, pointsRequest);

      if (!response.ok) {
        const data = await response.json();
        reportError(response.status, data?.message ?? this.defaultError.message);
        return;
      }

      const pointsContent = await response.text();
      this.rewards.outerHTML = pointsContent;

      this.parseElements();
      this.bindEvents();

      this.store.dispatch({
        type: REMOVE_POINTS,
      });

      trackEvent({
        event: "checkout",
        category: "Checkout",
        action: "Remove R4R Points",
        label: numberOfPoints,
      });
    }
  };

  handleDeliveryChange = async () => {
    await this.removeAppliedRewards();
    await this.refreshRewards();
  };

  handleDiscountChange = async () => {
    await this.refreshRewards();
  };

  handlePointsChange = () => {
    const { numberOfPoints } = this.getSelectedPoints();
    this.remainingPoints.innerHTML = String(this.totalPoints - numberOfPoints);
  };

  handleApplyPoints = async () => {
    try {
      startLoadingState(this.addingPointsMessage, ".basket-overlay");

      const { numberOfPoints, amount } = this.getSelectedPoints();

      const pointsRequest = { points: numberOfPoints, email: this.emailInput.value };
      const response = await postJsonWithResponse(constants.urls.payment.reward_points, pointsRequest);

      if (!response.ok) {
        reportError(response.status, this.defaultError.message);
        const text = await response.text();

        // Try parsing JSON safely
        let message: string;
        try {
          const data = JSON.parse(text);
          message = data.message ?? this.defaultError.message; // fallback to raw text if no message field
        } catch {
          message = this.defaultError.message; // fallback if not valid JSON
        }

        reportError(response.status, message);
        endLoadingState(".basket-overlay");
        return;
      }

      const pointsContent = await response.text();

      this.rewards = document.querySelector(this.rewardsSelector);
      if (!this.rewards) return;

      this.rewards.outerHTML = pointsContent;

      this.parseElements();
      this.bindEvents();
      EventEmitter.emit(events.R4R_REFRESH);

      this.store.dispatch({
        type: ADD_POINTS,
        payload: {
          numberOfPoints: numberOfPoints,
          amount,
        },
      });

      trackEvent({
        event: "checkout",
        category: "Checkout",
        action: "Add R4R Points",
        label: numberOfPoints,
      });

      this.rewards.focus();
    } catch (e) {
      reportError(this.defaultError.code, e.message ?? this.defaultError.message);
      endLoadingState(".basket-overlay");
    }
  };

  handleRemovePoints = async (e: Event) => {
    try {
      if ("pointsRemove" in (e.target as HTMLElement).dataset) {
        EventEmitter.emit(events.ORDER_UPDATE_START);

        await this.removeAppliedRewards();
        EventEmitter.emit(events.R4R_REFRESH);

        this.rewards.focus();
      }
    } catch (e) {
      console.log(e);
      reportError("An error occurred. Please try again later.");
      EventEmitter.emit(events.ORDER_UPDATE_END);
    }
  };
}
